/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect } from 'react';

// Mantine
import {
	Box,
	Group,
	Image,
	Skeleton,
} from '@mantine/core';

// Components
import { SelectionCart } from '@components/molecules/SelectionCart';
import { CardProject } from '@components/molecules/CardProject';
import { MenuFilter } from '@components/molecules/MenuFilter';
import { AddFiltersButton } from '@components/atoms/AddFiltersButton';
import { Pagination } from '@components/atoms/Pagination';
import { useOffsetProjectAdditional } from '@features/offsets/hooks/useOffsetProjectAdditional';

// Styles
import classes from './Step3.module.css';

// Mock
import mocks from '../../../../mocks/projects.json';

// Icons
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';

// Utils
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';

// Store
import useStore from '@store/index';

// Constants
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';

// Api
import { useAdditinalProjects } from '@features/offsets/api/get-additional-projects';
import { useAdditionalities } from 'shared/api/get-additionalities';
import { convertArrayToLabelValue } from '@utils/functions';
import { useTranslation } from 'react-i18next';
import { ACTIVATED_FILTER_OPTIONS } from '@utils/options';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const Step3 = () => {
	/* ********************************** Hooks ********************************* */
	const {
		offsetCreationDatas,
		setOffsetsAdditionalProjects,
		offsetProducedAddis,
		setOffsetAdditionalProjectsPage,
		setOffsetAdditionalProjectsPageSize,
		offsetAdditionalProjectsPage,
		offsetAdditionalProjectsPageSize,
		offsetAdditionalProjectsQueryParams,
		offsetAdditionalProjects,
		offsetAdditionalProjectsSelected,
		setOffsetAdditionalProjects,
		setOffsetStep3Error,
		setIsEnabledOffsetNestStep,
	} = useStore();

	useEffect(() => {
		if (mocks.projects && Array.isArray(mocks.projects)) {
			setOffsetsAdditionalProjects(mocks.projects);
		}
	}, []);

	const {
		handleProjectFilter,
		handleCountryFilter,
		handleTechnologyFilter,
		isSelectedProjectAdditional,
		handleRemoveSelectedProject,
		handleNextStep,
		handleAddProject,
		handleOnChangePrice,
		handleActiveFilter,
	} = useOffsetProjectAdditional();

	// APIs
	const { data: projects } = useAdditionalities();

	const {
		data: offsetsAdditionalProjectsApi,
		isLoading,
		total: totalItems,
		totalPages,
		isRefetching,
		isFetched,
	} = useAdditinalProjects(
		offsetAdditionalProjectsPage,
		offsetAdditionalProjectsPageSize,
		offsetAdditionalProjectsQueryParams
	);

	useEffect(() => {
		if (isRefetching && offsetsAdditionalProjectsApi) {
			setOffsetAdditionalProjects(
				offsetsAdditionalProjectsApi
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRefetching]);

	useEffect(() => {
		if (
			isFetched &&
			!isRefetching &&
			offsetsAdditionalProjectsApi
		) {
			setOffsetAdditionalProjects(
				offsetsAdditionalProjectsApi
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFetched]);

	const { t } = useTranslation();
	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.content}>
				<Group className={classes.row}>
					<Box className={classes.containerFilter}>
						<MenuFilter
							isPersist
							value={
								offsetAdditionalProjectsQueryParams._id
									? offsetAdditionalProjectsQueryParams._id
									: []
							}
							onApply={handleProjectFilter}
							label={t('global.project')}
							size="lg"
							options={convertArrayToLabelValue(projects)}
							placeholderSearch={t(
								'filters.placeholderSearch.searchProject'
							)}
							placeholder={t(
								'filters.placeholders.selectProject'
							)}
						/>
					</Box>
					<Box className={classes.containerFilter}>
						<MenuFilter
							isPersist
							value={
								offsetAdditionalProjectsQueryParams[
									'location.countryCode'
								]
									? offsetAdditionalProjectsQueryParams[
											'location.countryCode'
										]['$in']
									: []
							}
							onApply={handleCountryFilter}
							label={t('global.country')}
							size="lg"
							options={EUROPEAN_COUNTRIES}
							placeholderSearch={t(
								'filters.placeholderSearch.searchCountry'
							)}
							placeholder={t(
								'filters.placeholders.selectCountry'
							)}
						/>
					</Box>
					<Box className={classes.containerFilter}>
						<MenuFilter
							isPersist
							value={
								offsetAdditionalProjectsQueryParams.technology
									? offsetAdditionalProjectsQueryParams.technology
									: []
							}
							onApply={handleTechnologyFilter}
							label={t('global.projectType')}
							size="lg"
							options={PROJECT_TECHNOLOGIES}
							placeholderSearch={t(
								'filters.placeholderSearch.searchTechnology'
							)}
							placeholder={t(
								'filters.placeholders.selectTechnology'
							)}
						/>
					</Box>
					<AddFiltersButton>
						{t('global.addFilters')}
					</AddFiltersButton>
				</Group>
				<Box className={classes.contentCards}>
					{isLoading &&
						Array.from(Array(5).keys()).map(() => (
							<Skeleton
								height={260}
								width={260}
								radius="md"
							/>
						))}
					{!isLoading &&
						offsetAdditionalProjects.length == 0 && (
							<Box className={classes.containerNoData}>
								<Image
									src={NoRecordsIcon}
									className={classes.iconNoRecords}
								/>
								{t('global.noData')}
							</Box>
						)}
					{!isLoading &&
						offsetAdditionalProjects.length > 0 &&
						offsetAdditionalProjects.map(
							(project: any) => (
								<CardProject
									key={project.id}
									size={'sm'}
									type={'additionality'}
									data={project}
									tco2={0}
									disabled={project.isDisabled}
									disabledLabel={t(
										'offsets.financingRequirementAchieved'
									)}
									isAdded={isSelectedProjectAdditional(
										project.id ? project.id : '',
										offsetAdditionalProjectsSelected
									)}
									onAdd={() => {
										handleAddProject(project);
									}}
									onChangeTco2={() => {}}
								/>
							)
						)}
				</Box>
				{/* Pagination */}
				<Box className={classes.containerPagination}>
					<Pagination
						pagesize={offsetAdditionalProjectsPageSize}
						totalitems={totalItems}
						activepage={offsetAdditionalProjectsPage}
						handlechangesize={function (
							_size: string | number
						) {
							setOffsetAdditionalProjectsPageSize(_size);
							setOffsetAdditionalProjectsPage(1);
						}}
						onChange={(data) => {
							setOffsetAdditionalProjectsPage(data);
						}}
						total={totalPages ? totalPages : 0}
					/>
				</Box>
			</Box>
			<Box className={classes.cartBar}>
				<SelectionCart
					price={offsetProducedAddis}
					handleConfirm={handleNextStep}
					handlechangeprice={(_item, _price) => {
						handleOnChangePrice(_item, _price);
					}}
					items={offsetAdditionalProjectsSelected}
					onRemove={handleRemoveSelectedProject}
					size={'sm'}
					title={t('global.projects')}
					type={'prices'}
					max={offsetCreationDatas.quantity}
					handlechangequantity={() => {}}
					setError={(isError: boolean) => {
						setOffsetStep3Error(isError);
					}}
					onchangestatus={(_status: boolean) => {
						setIsEnabledOffsetNestStep(_status);
					}}
				/>
			</Box>
		</Box>
	);
};
